import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Collapse
} from '@mui/material';
import { Edit, Check, Close } from '@mui/icons-material';
import { updateUsername } from '../services/userService';
import { validateUsername } from '../utils/usernameUtils';

const UsernameEditor = ({ currentUsername, userId, onUsernameUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newUsername, setNewUsername] = useState(currentUsername);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleStartEdit = () => {
    setIsEditing(true);
    setNewUsername(currentUsername);
    setError('');
    setSuccess(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewUsername(currentUsername);
    setError('');
  };

  const handleSaveUsername = async () => {
    const validation = validateUsername(newUsername);
    
    if (!validation.isValid) {
      setError(validation.errors[0]);
      return;
    }

    if (newUsername === currentUsername) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await updateUsername(userId, newUsername);
      setSuccess(true);
      setIsEditing(false);
      onUsernameUpdate(newUsername);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError(error.message || 'Failed to update username');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveUsername();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Username
      </Typography>
      
      {!isEditing ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body1" sx={{ 
            fontFamily: 'monospace', 
            bgcolor: 'grey.100', 
            px: 1, 
            py: 0.5, 
            borderRadius: 1,
            fontSize: '1.1rem'
          }}>
            @{currentUsername}
          </Typography>
          <IconButton 
            onClick={handleStartEdit}
            size="small"
            sx={{ color: 'primary.main' }}
          >
            <Edit />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            onKeyDown={handleKeyPress}
            variant="outlined"
            size="small"
            autoFocus
            disabled={isLoading}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>@</Typography>,
            }}
            sx={{ minWidth: 200 }}
          />
          <IconButton 
            onClick={handleSaveUsername}
            disabled={isLoading}
            color="primary"
            size="small"
          >
            {isLoading ? <CircularProgress size={20} /> : <Check />}
          </IconButton>
          <IconButton 
            onClick={handleCancelEdit}
            disabled={isLoading}
            color="secondary"
            size="small"
          >
            <Close />
          </IconButton>
        </Box>
      )}

      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      </Collapse>

      <Collapse in={success}>
        <Alert severity="success" sx={{ mt: 1 }}>
          Username updated successfully!
        </Alert>
      </Collapse>

      {isEditing && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Press Enter to save, Escape to cancel
        </Typography>
      )}
    </Box>
  );
};

export default UsernameEditor;
